import { useCallback, useEffect, useState } from 'react';
import ScrollView from 'devextreme-react/scroll-view';
import Sortable from 'devextreme-react/sortable';
import { AddEvent, ReorderEvent } from 'devextreme/ui/sortable';
import { Input, Button, Tooltip } from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import './BoardTicketPage.scss';
import { findAllRole, findAllStatus, findAllTicket, findAllTitle, findAllTitleDetail, updateTicket } from 'components/Mytask/mtDetails/mtDetails-Content/WorkTicket/api';
import SelectBox from 'devextreme-react/select-box';
import { ExtendedTicketInformation, Role, Status, TicketInformation, Title, TitleDetail } from 'components/Mytask/mtDetails/mtDetails-Content/WorkTicket/interface';
import { brds } from './interface';
import ListTicket from 'components/Ticket/ListTicket';


const getLists = (
    statusArray: Status[],
    ticketArray: TicketInformation[],
    roles: Role[],
    titleDetails: TitleDetail[]
): ExtendedTicketInformation[][] => {
    const rolesMap = roles.reduce((map: Record<string, string>, role) => {
        map[role.id.toString()] = role.roleName;
        return map;
    }, {});

    const titleDetailsMap = titleDetails.reduce((map: Record<string, string>, detail) => {
        map[detail.id.toString()] = detail.titleDetailName;
        return map;
    }, {});

    const ticketsMap = ticketArray.reduce((result: Record<string, ExtendedTicketInformation[]>, ticket) => {
        const status = statusArray.find((s) => s.id === ticket.statusId)?.statusName || 'Open';
        if (!result[status]) result[status] = [];
        const roleIdKey = ticket.roleId != null ? ticket.roleId.toString() : '';
        const titleDetailIdKey = ticket.titleDetailId != null ? ticket.titleDetailId.toString() : '';

        // result[status].push({
        //     ...ticket,
        //     roleName: rolesMap[roleIdKey] || 'Unknown Role',
        //     titleDetailName: titleDetailsMap[titleDetailIdKey] || 'Unknown Title Detail',
        //     resource: (ticket as any).resource  || { id: ticket.userId, name: 'Unknown User' }, // Pass resource or fallback
        // });
        return result;
    }, {});

    return statusArray.map((status) => ticketsMap[status.statusName] || []);
};

const removeItem = <T,>(array: T[], index: number): T[] => array.filter((_, i) => i !== index);
const insertItem = <T,>(array: T[], item: T, index: number): T[] => {
    const newArr = [...array];
    newArr.splice(index, 0, item);
    return newArr;
};
const reorderItem = <T,>(array: T[], fromIdx: number, toIdx: number): T[] => {
    const item = array[fromIdx];
    const result = removeItem(array, fromIdx);
    return insertItem(result, item, toIdx);
};

const BoardTicketPage = () => {
    const [searchUser, setSearchUser] = useState('');
    const [searchTask, setSearchTask] = useState('');
    const [lists, setLists] = useState<ExtendedTicketInformation[][]>([]);
    const [ticketInformations, setTicketInformations] = useState<TicketInformation[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [statuses, setStatuses] = useState<Status[]>([]);
    const [titles, setTitles] = useState<Title[]>([]);
    const [titleDetails, setTitleDetails] = useState<TitleDetail[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const allRoles = await findAllRole();
                const allStatuses = await findAllStatus();
                const allTitles = await findAllTitle();
                const allTitleDetails = await findAllTitleDetail();
                const allTickets = await findAllTicket();

                setRoles(allRoles);
                setStatuses(allStatuses);
                setTitles(allTitles);
                setTitleDetails(allTitleDetails);
                setTicketInformations(allTickets);

                setLists(getLists(allStatuses, allTickets, allRoles, allTitleDetails));
            } catch (error) {
                console.error("Failed to fetch data", error);
            }
        };
        fetchData();
    }, []);

    const onListReorder = useCallback(({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
        setLists(state => reorderItem(state, fromIndex, toIndex));
        setStatuses(state => reorderItem(state, fromIndex, toIndex));
    }, []);

    const onTicketDrop = useCallback(async (e: AddEvent | ReorderEvent) => {
        const fromData = e.fromData ?? 0;
        const toData = e.toData ?? 0;
        const fromIndex = e.fromIndex;
        const toIndex = e.toIndex;

        const updatedLists = [...lists];
        const item = updatedLists[fromData][fromIndex];
        updatedLists[fromData] = removeItem(updatedLists[fromData], fromIndex);
        updatedLists[toData] = insertItem(updatedLists[toData], item, toIndex);

        const updatedTicket = { ...item, statusId: statuses[toData].id };
        await updateTicket(item.id, updatedTicket);

        setLists(updatedLists);
    }, [lists, statuses]);


    const handleSearch = () => {
        const filtered = ticketInformations.filter((ticket) =>
            (ticket.user?.nickname || '').toLowerCase().includes(searchUser.toLowerCase()) &&
            ticket.taskId.toString().includes(searchTask.toLowerCase())
        );
        setLists(getLists(statuses, filtered, roles, titleDetails));
    };


    const handleReset = () => {
        setSearchUser('');
        setSearchTask('');
        setLists(getLists(statuses, ticketInformations, roles, titleDetails));
    };

    return (
        <div className="kanban-board">
            <div className="kanban-toolbar">
                <SelectBox
                    items={brds}
                    displayExpr="name"
                    valueExpr="id"
                    placeholder={"Tất cả"}
                />
                <Input placeholder="Search by user ID..." value={searchUser} onChange={e => setSearchUser(e.target.value)} />
                <Input placeholder="Search by task ID..." value={searchTask} onChange={e => setSearchTask(e.target.value)} />
                <Tooltip className="search" title="Search"><Button icon={<SearchOutlined />} onClick={handleSearch} /></Tooltip>
                <Tooltip className="search" title="Reset"><Button icon={<ReloadOutlined />} onClick={handleReset} /></Tooltip>
            </div>
            <ScrollView className="scrollable-board" direction="horizontal" showScrollbar="always">
                <Sortable className="sortable-lists" itemOrientation="horizontal" handle=".list-header" onReorder={onListReorder}>
                    {lists.map((tickets, index) => (
                        <ListTicket
                            key={statuses[index].id}
                            title={statuses[index].statusName}
                            index={index}
                            tickets={tickets}
                            onTicketDrop={onTicketDrop} />
                    ))}
                </Sortable>
            </ScrollView>
        </div>
    );
};
export default BoardTicketPage;